/**
 * Categories Service
 * Gestisce il caricamento e caching delle categorie dal backend
 */

import { backend } from './backend.js';

class CategoriesService {
  constructor() {
    this.categories = [];
    this.loaded = false;
    this.loading = false;
    this.loadPromise = null;
  }

  /**
   * Carica le categorie dal backend WordPress
   * @returns {Promise<Array>} Array di categorie
   */
  async loadCategories() {
    // Se già caricate, ritorna subito
    if (this.loaded) {
      return this.categories;
    }

    // Se già in caricamento, attendi il caricamento in corso
    if (this.loading) {
      return this.loadPromise;
    }

    // Inizia caricamento
    this.loading = true;
    this.loadPromise = this._fetchCategories();

    try {
      this.categories = await this.loadPromise;
      this.loaded = true;
      return this.categories;
    } catch (error) {
      console.error('❌ Failed to load categories:', error);
      // Usa fallback di default
      this.categories = this._getDefaultCategories();
      this.loaded = true;
      return this.categories;
    } finally {
      this.loading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Fetch interno delle categorie
   */
  async _fetchCategories() {
    try {
      const categories = await backend.categories.list();

      // Assicura che ogni categoria abbia icon e color di default
      return categories.map(cat => ({
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        count: cat.count || 0,
        icon: this._getIconForSlug(cat.slug),
        color: this._getColorForSlug(cat.slug),
        class: this._getClassForSlug(cat.slug)
      }));
    } catch (error) {
      console.error('Error fetching from API:', error);
      throw error;
    }
  }

  /**
   * Categorie di default come fallback - IDs corretti da WordPress
   */
  _getDefaultCategories() {
    return [
      { id: 10, name: 'Dove Mangiare', slug: 'mangiare', count: 0, icon: '🍽️', color: 'primary', class: 'badge-primary' },
      { id: 11, name: 'Dove Dormire', slug: 'dormire', count: 0, icon: '🏨', color: 'success', class: 'badge-success' },
      { id: 12, name: 'Dove Comprare', slug: 'comprare', count: 0, icon: '🛍️', color: 'warning', class: 'badge-warning' },
      { id: 21, name: 'Cosa Vedere', slug: 'vedere', count: 0, icon: '🏛️', color: 'info', class: 'badge-info' },
      { id: 22, name: 'Associazioni', slug: 'associazioni', count: 0, icon: '🤝', color: 'secondary', class: 'badge-secondary' }
    ];
  }

  /**
   * Ottiene icon per slug
   */
  _getIconForSlug(slug) {
    const icons = {
      'mangiare': '🍽️',
      'dormire': '🏨',
      'comprare': '🛍️',
      'vedere': '🏛️',
      'associazioni': '🤝',
      'servizi': '🔧'
    };
    return icons[slug] || '📍';
  }

  /**
   * Ottiene color class per slug
   */
  _getColorForSlug(slug) {
    const colors = {
      'mangiare': 'primary',
      'dormire': 'success',
      'comprare': 'warning',
      'vedere': 'info',
      'associazioni': 'secondary',
      'servizi': 'dark'
    };
    return colors[slug] || 'light';
  }

  /**
   * Ottiene badge class per slug
   */
  _getClassForSlug(slug) {
    return `badge-${this._getColorForSlug(slug)}`;
  }

  /**
   * Ottiene tutte le categorie (carica se necessario)
   * @returns {Promise<Array>}
   */
  async getCategories() {
    if (!this.loaded) {
      await this.loadCategories();
    }
    return this.categories;
  }

  /**
   * Ottiene una categoria per ID
   * @param {number} id - ID della categoria
   * @returns {Object|null}
   */
  getCategoryById(id) {
    return this.categories.find(cat => cat.id === parseInt(id)) || null;
  }

  /**
   * Ottiene una categoria per slug
   * @param {string} slug - Slug della categoria
   * @returns {Object|null}
   */
  getCategoryBySlug(slug) {
    return this.categories.find(cat => cat.slug === slug) || null;
  }

  /**
   * Ottiene l'ID di una categoria dal suo slug
   * @param {string} slug - Slug della categoria
   * @returns {number|null}
   */
  getCategoryId(slug) {
    const category = this.getCategoryBySlug(slug);
    return category ? category.id : null;
  }

  /**
   * Ottiene il nome di una categoria dal suo slug
   * @param {string} slug - Slug della categoria
   * @returns {string}
   */
  getCategoryName(slug) {
    const category = this.getCategoryBySlug(slug);
    return category ? category.name : slug;
  }

  /**
   * Ottiene l'icon di una categoria dal suo slug
   * @param {string} slug - Slug della categoria
   * @returns {string}
   */
  getCategoryIcon(slug) {
    const category = this.getCategoryBySlug(slug);
    return category ? category.icon : '📍';
  }

  /**
   * Ottiene la classe badge di una categoria dal suo slug
   * @param {string} slug - Slug della categoria
   * @returns {string}
   */
  getCategoryClass(slug) {
    const category = this.getCategoryBySlug(slug);
    return category ? category.class : 'badge-light';
  }

  /**
   * Verifica se le categorie sono state caricate
   * @returns {boolean}
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Forza il reload delle categorie
   * @returns {Promise<Array>}
   */
  async reload() {
    this.loaded = false;
    this.categories = [];
    return this.loadCategories();
  }
}

// Esporta istanza singleton
export default new CategoriesService();
